
import React, { FormEvent, useEffect, useState } from 'react';
import { Card, Form, Button, Col, Row } from "react-bootstrap";
import { v4 as uuidv4 } from 'uuid';
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import InputGroup from 'react-bootstrap/InputGroup';
import { Offer } from '../types/offerForm';
import { generateRandomNumbr } from '../utils/randomNumbers';
import { ValidationErrors } from '../validation/validationErrors';
import { validateForm } from '../validation/validationForm';
import InvoiceItem, { Item } from './InvoiceItem';
import { offerValidationSchema } from '../validation/offer/offerValidationSchema';
import { useFormState } from '../hooks/useStateHandler';
import DocumentModal from './DocumentModal';
import ShowOfferDetails from './ShowOfferDetails';
import { useOffers } from '../hooks/useOffers';
import { calculateSubTotal } from '../hooks/useDocumentForm';
import { GeneratePDF } from '../utils/documentHandler';




interface OfficeFormProps {
  offer?: Offer; // Define 'invoice' type properly
  isEdit?: boolean;
}

const OfferForm: React.FC<OfficeFormProps> = ({ offer, isEdit = false }) => {
  const navigate = useNavigate()
  const { t } = useTranslation();  
  const id = generateRandomNumbr();
  const { addOffer, editOffer } = useOffers()
  
  const uniqueId = offer?.id ? offer.id : `${t('inv')}-${id}`;

  // Initialize state 
const { state, setState, handleChange, setField } = useFormState<Offer>({

  id: offer?.id ? offer.id : id,
  iban: offer?.iban || '',
  validUntil: offer?.validUntil || '',
  madeBy: offer?.madeBy || '',
  currency: offer?.currency || t('euro'),
  offerNumber: offer?.offerNumber || uniqueId.toString(),
  to: offer?.to || '',
  toEmail: offer?.toEmail || '',
  toAddress: offer?.toAddress || '',
  from: offer?.from || '',
  fromEmail: offer?.fromEmail || '',
  fromAddress: offer?.fromAddress || '',
  toOib: offer?.toOib || '',
  fromOib: offer?.fromOib || '',
  remark: offer?.remark || '',
  total: offer?.total || '0.00',
  subTotal: offer?.subTotal || '0.00',
  taxRate: offer?.taxRate || '0.00',
  taxAmmount: offer?.taxAmmount || '0.00',
  discountRate: offer?.discountRate || '0.00',
  discountAmmount: offer?.discountAmmount || '0.00',
  items: offer?.items || [
    {
      id: '1',
      name: '',
      description: '',
      price: '0.00',
      quantity: 1,
    }
  ]
});
   
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationErrors<Offer>>({});  

// Recalculate totals only when items change
  useEffect(() => {  
    handleCalculateTotal();
  }, [state.items, state.taxRate, state.discountRate]);  // watch only when needed

  const onItemizedItemEdit = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    evt.preventDefault();
    const name = evt.target.name;
    const itemId = evt.target.id;
    const value = evt.target.value;
  
    const newItems = state.items.map((item) =>
      item.id === itemId ? { ...item, [name]: value } : item
    );
  
    setField('items', newItems);
  };
  
  // Handle changes to any input field
  const editField = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {    
    handleChange(event)
  };

  // Handle adding a new item
  const handleAddEvent = (evt: FormEvent) => {
    evt.preventDefault();
    const id = uuidv4().substr(0, 3);
    const newItem = {
      id,
      name: '',
      price: '1.00',
      description: '',
      quantity: 1
    };
    setState(prevState => ({
      ...prevState,
      items: [...prevState.items, newItem]
    }));
  };

  // Handle deleting an item
  const handleRowDel = (i: Item) => {
    setState(prevState => ({
      ...prevState,
      items: prevState.items.filter(item => item.id !== i.id)
    }));
  };

  const onCurrencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCurrency = event.target.value;
    setState(prevState => ({
      ...prevState,
      currency: selectedCurrency
    }));
  };

  // Calculate the total, subtotal, tax, and discount
  const handleCalculateTotal = () => {
    const subTotal = calculateSubTotal(state.items);       
    setState(prevState => ({
      ...prevState,
      subTotal: subTotal.toFixed(2),
      taxAmmount: (subTotal * parseFloat(prevState.taxRate) / 100).toFixed(2),
      discountAmmount: (subTotal * parseFloat(prevState.discountRate) / 100).toFixed(2),
      total: (subTotal - parseFloat(prevState.discountAmmount) + parseFloat(prevState.taxAmmount)).toFixed(2)
    }));
  };

  // Open modal
  const openModal = (event: FormEvent) => {
    event.preventDefault();
    const validationErrors = validateForm(state, offerValidationSchema, t); // Validate the form
    setErrors(validationErrors); // Update the error state
    if (Object.keys(validationErrors).length === 0) {
      // No errors, submit the form
      setIsOpen(true);
    }
  };

 const handleOnSave = () => {
     if(window.location.pathname.includes('edit')){
       editOffer(state)
    }else{      
      addOffer(state)
     }
     navigate("/offers")
   }
 
   const handleOnDownload = () => {
     GeneratePDF(state.offerNumber);
   }

  // Close modal
  const closeModal = () => setIsOpen(false);

  return (
    <>
      <Form onSubmit={openModal}>
        <Row>
          <Col md={8} lg={9}>
            <Card className="p-4 p-xl-5 my-3 my-xl-4">
              <div className="d-flex flex-row align-items-start justify-content-between mb-3">
                <div className="d-flex flex-column">
                  
                  <div className="d-flex flex-row align-items-center">
                    <span className="fw-bold d-block me-2">{t('validUntil')}:&nbsp;</span>
                    <Form.Control type="date" value={state.validUntil} name={"validUntil"} onChange={editField} style={{ maxWidth: '150px' }} required />
                  </div>
                </div>
                <div className="d-flex flex-row align-items-center">
                  <span className="fw-bold me-2">{t('offerNumber')}:&nbsp;{state.offerNumber }</span>
                </div>
              </div>
              <hr className="my-4" />
              <Row>
                <Col>
                  <Form.Label className="fw-bold">{t('to')}:</Form.Label>
                  <Form.Control placeholder={t('placeholder.to')} rows={3} as="textarea" value={state.to} type="text" name="to" className="my-2" onChange={editField} autoComplete="name" required />
                  <Form.Control placeholder={t('placeholder.toEmail')} value={state.toEmail} type="email" name="toEmail" className="my-2" onChange={editField} autoComplete="email" required />
                  <Form.Control placeholder={t('placeholder.toAddress')} value={state.toAddress} type="text" name="toAddress" className="my-2" autoComplete="address" onChange={editField} required />
                  <Form.Control placeholder={t('placeholder.toOib')} value={state.toOib} type="text" name="toOib" className="my-2" autoComplete="name" onChange={editField} required />
                  {errors.toOib && (
                     <span className="text-sm" style={{color: 'red'}}>{errors.toOib}</span>
                  )}

                </Col>
                <Col>
                  <Form.Label className="fw-bold">{t('from')}:</Form.Label>
                  <Form.Control placeholder={t('placeholder.from')} rows={3} as="textarea" value={state.from} type="text" name="from" className="my-2" onChange={editField} autoComplete="name" required />
                  <Form.Control placeholder={t('placeholder.fromEmail')} value={state.fromEmail} type="email" name="fromEmail" className="my-2" onChange={editField} autoComplete="email" required />
                  <Form.Control placeholder={t('placeholder.fromAddress')} value={state.fromAddress} type="text" name="fromAddress" className="my-2" autoComplete="address" onChange={editField} required />
                  <Form.Control placeholder={t('placeholder.fromOib')} value={state.fromOib} type="text" name="fromOib" className="my-2" autoComplete="name" onChange={editField} required />
                  {errors.fromOib && (
                     <span className="text-sm" style={{color: 'red'}}>{errors.fromOib}</span>
                  )}
                </Col>
              </Row>
              <Row  className="mb-5">
                <Col>
                    <Form.Control placeholder={t('placeholder.iban')} value={state.iban} type="text" name="iban" className="my-2" autoComplete="name" onChange={editField} required />
                    {errors.iban && (
                        <span className="text-sm" style={{color: 'red'}}>{errors.iban}</span>
                    )}
                    <Form.Control placeholder={t('placeholder.madeBy')} value={state.madeBy} type="text" name="madeBy" className="my-2" autoComplete="name" onChange={editField} />
                  </Col>
              </Row>
              <InvoiceItem
                onItemizedItemEdit={onItemizedItemEdit}
                onRowAdd={handleAddEvent}
                onRowDel={handleRowDel}
                currency={state.currency}
                items={state.items}
              />
              <Row className="mt-4 justify-content-end">
                <Col lg={6}>
                  <div className="d-flex flex-row align-items-start justify-content-between">
                    <span className="fw-bold">{t('subTotal')}:</span>
                    <span>{state.currency}{state.subTotal}</span>
                  </div>
                  <div className="d-flex flex-row align-items-start justify-content-between mt-2">
                    <span className="fw-bold">{t('discount')}:</span>
                    <span>
                      <span className="small">({state.discountRate || 0}%)</span>
                      {state.currency}{state.discountAmmount || 0}</span>
                  </div>
                  <div className="d-flex flex-row align-items-start justify-content-between mt-2">
                    <span className="fw-bold">{t('tax')}:</span>
                    <span>
                      <span className="small">({state.taxRate || 0}%)</span>
                      {state.currency}{state.taxAmmount || 0}</span>
                  </div>
                  <hr />
                  <div className="d-flex flex-row align-items-start justify-content-between" style={{ fontSize: '1.125rem' }}>
                    <span className="fw-bold">{t('total')}:</span>
                    <span className="fw-bold">{state.currency}{state.total || 0}</span>
                  </div>
                </Col>
              </Row>
              <hr className="my-4" />
              <Form.Label className="fw-bold">{t('remark')}:</Form.Label>
              <Form.Control placeholder={t('placeholder.remark')} name="remark" value={state.remark} onChange={editField} as="textarea" className="my-2" rows={1} />
            </Card>
          </Col>
          <Col md={4} lg={3}>
            <div className="sticky-top pt-md-3 pt-xl-4">
              <Button variant="primary" type="submit" className="d-block w-100 mb-2">{t('reviewOffer')}</Button>
              <Link to="/">
                <Button variant="danger" className="d-block w-100">{t('cancelAndBackHome')}</Button>
              </Link>

              <DocumentModal showModal={isOpen} closeModal={closeModal} onDownload={handleOnDownload} onSave={ handleOnSave } childProps={state}>
                {(childProps) => (
                      <ShowOfferDetails offer={childProps} />
                )}
              </DocumentModal>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('currency')}:</Form.Label>
                <Form.Select value={state.currency} onChange={onCurrencyChange} className="btn btn-light my-1" aria-label="Change Currency">
                  <option value="€">EUR (Euro)</option>
                  <option value="$">USD (United States Dollar)</option>
                  <option value="£">GBP (Pound Sterling)</option>
                  <option value="€">EUR (Euro)</option>                                        
                  <option value="¥">JPY (Japanese Yen)</option>
                  <option value="$">CAD (Canadian Dollar)</option>
                  <option value="$">AUD (Australian Dollar)</option>
                  <option value="$">SGD (Signapore Dollar)</option>
                  <option value="¥">CNY (Chinese Renminbi)</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="my-3">
                  <Form.Label className="fw-bold">{t('tax')}:</Form.Label>
                  <InputGroup className="my-1 flex-nowrap">
                    <Form.Control name="taxRate" type="number" value={state.taxRate} onChange={(event) => editField(event)} className="bg-white border" placeholder="0.0" min="0.00" step="0.01" max="100.00" />
                    <InputGroup.Text className="bg-light fw-bold text-secondary small">%</InputGroup.Text>                    
                  </InputGroup>
                  {errors.taxRate && (
                        <span className="text-sm" style={{color: 'red'}}>{errors.taxRate}</span>
                    )}
              </Form.Group>
              <Form.Group className="my-3">
                <Form.Label className="fw-bold">{t('discount')}:</Form.Label>
                <InputGroup className="my-1 flex-nowrap">
                  <Form.Control name="discountRate" type="number" value={state.discountRate} onChange={(event) => editField(event)} className="bg-white border" placeholder="0.0" min="0.00" step="0.01" max="100.00" />
                  <InputGroup.Text className="bg-light fw-bold text-secondary small">%</InputGroup.Text>                  
                </InputGroup>
                {errors.discountRate && (
                        <span className="text-sm" style={{color: 'red'}}>{errors.discountRate}</span>
                    )}
              </Form.Group>              
            </div>
          </Col>
        </Row>
      </Form>
      
    </>
  );
};

export default OfferForm;

